import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  Button,
  HStack,
  useToast,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import Card from 'components/card/Card.js';
import { getData, postData } from 'utils/httpClient';

/*
  ACTA del dictado :id - "modo libre".

  Diseño (feedback cliente):
    - Una sola fila por alumno.
    - Inputs editables: Nota práctico (si la materia tiene práctico),
      Nota teórico, Nota final.
    - Select editable para Estado: CURSANDO / APROBADO / EXONERADO /
      NO_APROBADO.
    - Un único botón "Guardar" por fila que manda todos los campos a la
      vez.

  Reglas de negocio que se aplican (en backend, no en la UI):
    - Escala de notas 0-12.
    - Si la materia NO tiene parte práctica, la nota práctica no se
      acepta (el input queda deshabilitado aquí de forma preventiva).
    - Art. 22: si hay nota práctica < 4, no se permite cerrar el curso
      como APROBADO o EXONERADO. El backend rechaza esa combinación.
    - Al cerrar APROBADO/EXONERADO el backend setea validez 8 semestres.

  Endpoint usado:
    POST /dictated-courses/:id/acta/:studentCourseId/full
    body: { practicalGrade?, theoreticalGrade?, finalGrade?, status? }
*/

const STATUS_OPTIONS = [
  { value: 'CURSANDO', label: 'Cursando' },
  { value: 'APROBADO', label: 'Aprobado' },
  { value: 'EXONERADO', label: 'Exonerado' },
  { value: 'NO_APROBADO', label: 'No aprobado' },
];

const STATUS_COLORS = {
  CURSANDO: 'yellow',
  APROBADO: 'green',
  EXONERADO: 'blue',
  NO_APROBADO: 'red',
};

// Infiere estado sugerido a partir de la nota final según la escala 0-12.
// Solo se usa como ayuda visual al tipear; el usuario siempre puede
// override manualmente con el select.
const inferStatusFromFinal = (grade) => {
  if (grade === '' || grade === null || grade === undefined) return null;
  const n = Number(grade);
  if (Number.isNaN(n)) return null;
  if (n <= 3) return 'NO_APROBADO';
  if (n <= 8) return 'APROBADO';
  return 'EXONERADO';
};

export default function DictatedCourseActaView() {
  const { id } = useParams();
  const toast = useToast();

  const [dictated, setDictated] = useState(null);
  const [students, setStudents] = useState([]);
  // Estado editable por fila:
  //   { [studentCourseId]: { practicalGrade, theoreticalGrade,
  //                          finalGrade, status } }
  const [rows, setRows] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Metadata de cierre del acta: { closedAt, closedBy } | null.
  // Cuando closedAt != null, toda la UI se vuelve read-only.
  const [closed, setClosed] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const cancelRef = React.useRef();

  const load = useCallback(async () => {
    try {
      const [dc, ins, acta] = await Promise.all([
        getData(`/dictated-courses/${id}`),
        getData(`/course-inscriptions/course/${id}`),
        getData(`/dictated-courses/${id}/acta`),
      ]);
      setDictated(dc?.payload || null);
      const inscriptions = ins?.payload?.inscriptions || [];
      setStudents(inscriptions);
      setClosed(acta?.payload?.closed?.closedAt ? acta.payload.closed : null);

      // Precargamos las filas con lo que haya guardado en course_grade
      // para que el usuario vea las notas y el estado actual y pueda
      // editar cualquier campo sin pasos intermedios.
      const gradesMap = {};
      (acta?.payload?.grades || []).forEach((g) => {
        gradesMap[g.studentCourseId] = g;
      });
      const initialRows = {};
      inscriptions.forEach((i) => {
        const g = gradesMap[i.id] || {};
        initialRows[i.id] = {
          practicalGrade: g.practicalGrade ?? '',
          theoreticalGrade: g.theoreticalGrade ?? '',
          finalGrade: g.finalGrade ?? '',
          status: g.status || 'CURSANDO',
        };
      });
      setRows(initialRows);
    } catch (error) {
      toast({
        title: 'Error al cargar acta',
        description: error?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [id, toast]);

  useEffect(() => {
    if (id) load();
  }, [id, load]);

  const hasPractic = !!dictated?.subject?.practic;

  const updateRow = (insId, patch) => {
    setRows((prev) => ({
      ...prev,
      [insId]: { ...(prev[insId] || {}), ...patch },
    }));
  };

  // Handler para cambios en la nota final: además de setear la nota,
  // autocompleta el estado sugerido siguiendo la escala, pero solo si
  // el usuario aún no lo tocó manualmente (status vacío o CURSANDO).
  const handleFinalChange = (insId, value) => {
    setRows((prev) => {
      const current = prev[insId] || {};
      const suggested = inferStatusFromFinal(value);
      const nextStatus =
        !current.status || current.status === 'CURSANDO'
          ? suggested || current.status || 'CURSANDO'
          : current.status;
      return {
        ...prev,
        [insId]: {
          ...current,
          finalGrade: value,
          status: nextStatus,
        },
      };
    });
  };

  // Dispara el cierre del acta en backend. Si hay alumnos sin
  // resolución (CURSANDO) o faltan notas obligatorias, backend
  // responde 400 con la lista de motivos y se muestra tal cual.
  const closeActa = async () => {
    try {
      setClosing(true);
      const res = await postData(`/dictated-courses/${id}/acta/close`, {});
      if (res?.payload?.closedAt) {
        setClosed({
          closedAt: res.payload.closedAt,
          closedBy: res.payload.closedBy,
        });
      }
      toast({ title: 'Acta cerrada', status: 'success' });
      setConfirmOpen(false);
    } catch (error) {
      toast({
        title: 'No se pudo cerrar el acta',
        description: error?.message || 'Revise pendientes.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setClosing(false);
    }
  };

  const saveRow = async (insId) => {
    const r = rows[insId];
    if (!r) return;
    try {
      setSavingId(insId);
      const res = await postData(
        `/dictated-courses/${id}/acta/${insId}/full`,
        {
          // Mandamos siempre los 3 campos (vacío => null => "limpiar").
          practicalGrade:
            r.practicalGrade === '' ? null : r.practicalGrade,
          theoreticalGrade:
            r.theoreticalGrade === '' ? null : r.theoreticalGrade,
          finalGrade: r.finalGrade === '' ? null : r.finalGrade,
          status: r.status || 'CURSANDO',
        },
      );
      if (res?.payload) {
        // Sincronizamos con lo que devolvió el backend para reflejar
        // cualquier normalización (ej: reglas que ajusten el estado).
        updateRow(insId, {
          practicalGrade: res.payload.practicalGrade ?? '',
          theoreticalGrade: res.payload.theoreticalGrade ?? '',
          finalGrade: res.payload.finalGrade ?? '',
          status: res.payload.status || 'CURSANDO',
        });
      }
      toast({ title: 'Acta guardada', status: 'success' });
    } catch (error) {
      toast({
        title: 'No se pudo guardar el acta',
        description: error?.message || 'Revise las notas y el estado.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={5}>
        <HStack justify="space-between" mb={4} align="center" flexWrap="wrap">
          <HStack spacing={3} align="center">
            <Heading size="md">Acta - Dictado #{id}</Heading>
            {dictated?.subject?.description && (
              <Text color="gray.500">
                {dictated.subject.description}
                {hasPractic ? ' (con práctico)' : ''}
              </Text>
            )}
            {closed?.closedAt && (
              <Badge colorScheme="purple" fontSize="0.8em">
                ACTA CERRADA
              </Badge>
            )}
          </HStack>
          <HStack>
            <Button
              size="sm"
              colorScheme="purple"
              onClick={() => setConfirmOpen(true)}
              isDisabled={!!closed?.closedAt || students.length === 0}
              title={
                closed?.closedAt
                  ? 'El acta ya fue cerrada'
                  : 'Cierra el acta; no se podrá editar después'
              }
            >
              Cerrar acta
            </Button>
          </HStack>
        </HStack>

        {closed?.closedAt ? (
          <Text color="purple.600" mb={4} fontSize="sm">
            El acta fue cerrada el{' '}
            {new Date(closed.closedAt).toLocaleString()}. No se pueden
            modificar notas ni estados.
          </Text>
        ) : (
          <Text color="gray.500" mb={4} fontSize="sm">
            Escala 0-12. Sugerido: 0-3 No aprobado, 4-8 Aprobado, 9-12
            Exonerado. El estado se puede modificar manualmente. Al cerrar el
            acta se congelan todas las notas y se habilita la inscripción a
            examen de los alumnos aprobados/exonerados.
          </Text>
        )}

        {students.length === 0 ? (
          <Text>No hay inscriptos en este dictado.</Text>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Estudiante</Th>
                  <Th>Nota práctico</Th>
                  <Th>Nota teórico</Th>
                  <Th>Nota final</Th>
                  <Th>Estado</Th>
                  <Th>Acción</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((ins) => {
                  const r = rows[ins.id] || {};
                  return (
                    <Tr key={ins.id}>
                      <Td>
                        {ins?.student?.person?.name}{' '}
                        {ins?.student?.person?.lastName}
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          min={0}
                          max={12}
                          step="0.01"
                          size="sm"
                          maxW="100px"
                          value={r.practicalGrade ?? ''}
                          isDisabled={!hasPractic || !!closed?.closedAt}
                          placeholder={hasPractic ? '0-12' : 'N/A'}
                          onChange={(e) =>
                            updateRow(ins.id, {
                              practicalGrade: e.target.value,
                            })
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          min={0}
                          max={12}
                          step="0.01"
                          size="sm"
                          maxW="100px"
                          value={r.theoreticalGrade ?? ''}
                          placeholder="0-12"
                          isDisabled={!!closed?.closedAt}
                          onChange={(e) =>
                            updateRow(ins.id, {
                              theoreticalGrade: e.target.value,
                            })
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          min={0}
                          max={12}
                          step="0.01"
                          size="sm"
                          maxW="100px"
                          value={r.finalGrade ?? ''}
                          placeholder="0-12"
                          isDisabled={!!closed?.closedAt}
                          onChange={(e) =>
                            handleFinalChange(ins.id, e.target.value)
                          }
                        />
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Select
                            size="sm"
                            maxW="170px"
                            value={r.status || 'CURSANDO'}
                            isDisabled={!!closed?.closedAt}
                            onChange={(e) =>
                              updateRow(ins.id, { status: e.target.value })
                            }
                          >
                            {STATUS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                          <Badge
                            colorScheme={STATUS_COLORS[r.status] || 'gray'}
                          >
                            {r.status || 'CURSANDO'}
                          </Badge>
                        </HStack>
                      </Td>
                      <Td>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          isDisabled={savingId === ins.id || !!closed?.closedAt}
                          onClick={() => saveRow(ins.id)}
                        >
                          Guardar
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Confirmación explícita antes de cerrar el acta, por ser una
          acción irreversible. */}
      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cerrar acta
            </AlertDialogHeader>
            <AlertDialogBody>
              Una vez cerrada, el acta no se podrá volver a editar. Los
              alumnos con estado APROBADO o EXONERADO quedarán habilitados
              para rendir examen (validez 8 semestres). ¿Confirmás?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)}>
                Cancelar
              </Button>
              <Button
                colorScheme="purple"
                onClick={closeActa}
                ml={3}
              >
                Cerrar acta
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
